/**
 * レイヤー素材の LayerStack (docs/features/motion-graphics-mode-plan.md §3.3)。
 * backend (backend/services/motion_layered/parse_svg.py) が作り、素材の
 * `generation_metadata.layer_stack` に保存する。FE はここで形を検証してから使う
 * (素材 API の行 → GenerationAsset.layerStack・選択済み素材パネルのレイヤー一覧)。
 */

export type LayerStackLayer = {
  /** 重ね順 (下 → 上)。0 始まり */
  index: number;
  name: string;
  /** カンバス座標 (viewBox の単位・原点は canvas.originX / originY) の矩形 */
  x: number;
  y: number;
  width: number;
  height: number;
  /** 情報用 (runtime は適用しない) */
  opacity: number;
  src: string;
  /** そのレイヤーに現れる塗り色 (SVG のみ・PNG のレイヤーは空) */
  fills: string[];
  /**
   * 実体の種類。'png' = 画像 (写真・埋め込み PNG / JPEG) を含むためラスタとして受けたレイヤー (layer-<i>.png・
   * 色の一時変更 (tint)・線の描画 (draw) は使えない)。省略 (古い素材) = 'svg' (§3.3)
   */
  kind: LayerStackLayerKind;
  /** ルートからの経路 (描画要素の index の列)。古い素材には無い */
  path: number[] | null;
  /** 子が 2 つ以上のグループ (「グループを展開」できる) */
  expandable: boolean;
};

export type LayerStackLayerKind = 'svg' | 'png';

export type LayerStack = {
  version: number;
  format: 'svg' | 'raster';
  canvas: { width: number; height: number; originX: number; originY: number; scale: number };
  composite: { width: number; height: number; pxPerUnit: number };
  storage: { bucket: string; prefix: string; bytes: number } | null;
  background: string | null;
  layers: LayerStackLayer[];
  integrity: Record<string, unknown> | null;
  warnings: string[];
  source: { kind: string; filename: string | null; tool: string | null } | null;
  /** 展開済みのグループの経路 (backend の stack.expanded)。古い素材は [] */
  expanded: number[][];
};

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isPositive = (v: unknown): v is number => isFiniteNumber(v) && v > 0;

function parseLayer(raw: unknown): LayerStackLayer | null {
  if (!isObj(raw)) return null;
  const { index, name, x, y, width, height, src } = raw;
  if (!isFiniteNumber(index) || !Number.isInteger(index) || index < 0) return null;
  if (typeof name !== 'string') return null;
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) return null;
  // 幅・高さ 0 のレイヤーは backend が除外する (全画素 alpha=0)。負や非数は不正として扱う
  if (!isFiniteNumber(width) || !isFiniteNumber(height) || width < 0 || height < 0) return null;
  if (typeof src !== 'string' || !src) return null;
  const opacity = isFiniteNumber(raw.opacity) ? raw.opacity : 1;
  // kind は省略 = svg (古い素材)。未知の値は不正 (fail-closed)
  const kind = raw.kind === undefined ? 'svg' : raw.kind;
  if (kind !== 'svg' && kind !== 'png') return null;
  // PNG のレイヤーは塗り色を持たない (backend は [] を入れる)。万一入っていてもチップは出さない
  const fills = kind === 'png' || !Array.isArray(raw.fills) ? [] : raw.fills.filter((f): f is string => typeof f === 'string');
  const path = Array.isArray(raw.path) && raw.path.every((v) => isFiniteNumber(v) && Number.isInteger(v) && v >= 0)
    ? (raw.path as number[])
    : null;
  return { index, name, x, y, width, height, opacity, src, fills, kind, path, expandable: raw.expandable === true };
}

/**
 * `generation_metadata.layer_stack` を検証して LayerStack にする。形が崩れていれば null
 * (fail-closed: レイヤー素材として扱わない)。レイヤーが 1 枚でも不正なら全体を null にする。
 */
export function parseLayerStack(raw: unknown): LayerStack | null {
  if (!isObj(raw)) return null;
  const { canvas, composite, layers } = raw;
  const version = isFiniteNumber(raw.version) ? raw.version : null;
  if (version === null) return null;
  const format = raw.format === 'svg' || raw.format === 'raster' ? raw.format : null;
  if (!format) return null;
  if (!isObj(canvas) || !isPositive(canvas.width) || !isPositive(canvas.height)) return null;
  const originX = canvas.originX === undefined ? 0 : canvas.originX;
  const originY = canvas.originY === undefined ? 0 : canvas.originY;
  if (!isFiniteNumber(originX) || !isFiniteNumber(originY)) return null;
  if (!isObj(composite) || !isPositive(composite.width) || !isPositive(composite.height) || !isPositive(composite.pxPerUnit)) {
    return null;
  }
  if (!Array.isArray(layers) || layers.length === 0) return null;
  const parsedLayers: LayerStackLayer[] = [];
  for (const layer of layers) {
    const parsed = parseLayer(layer);
    if (!parsed) return null;
    parsedLayers.push(parsed);
  }
  // index は重ね順そのもの。0..n-1 を 1 回ずつ (重複・欠番は不正)
  const indexes = new Set(parsedLayers.map((l) => l.index));
  if (indexes.size !== parsedLayers.length) return null;
  if (parsedLayers.some((l) => l.index >= parsedLayers.length)) return null;
  const storage = isObj(raw.storage)
    && typeof raw.storage.bucket === 'string'
    && typeof raw.storage.prefix === 'string'
    ? {
        bucket: raw.storage.bucket,
        prefix: raw.storage.prefix,
        bytes: isFiniteNumber(raw.storage.bytes) ? raw.storage.bytes : 0,
      }
    : null;
  const source = isObj(raw.source) && typeof raw.source.kind === 'string'
    ? {
        kind: raw.source.kind,
        filename: typeof raw.source.filename === 'string' ? raw.source.filename : null,
        tool: typeof raw.source.tool === 'string' ? raw.source.tool : null,
      }
    : null;
  return {
    version,
    format,
    canvas: {
      width: canvas.width,
      height: canvas.height,
      originX,
      originY,
      scale: isPositive(canvas.scale) ? canvas.scale : 1,
    },
    composite: { width: composite.width, height: composite.height, pxPerUnit: composite.pxPerUnit },
    storage,
    background: typeof raw.background === 'string' ? raw.background : null,
    layers: parsedLayers,
    integrity: isObj(raw.integrity) ? raw.integrity : null,
    warnings: Array.isArray(raw.warnings) ? raw.warnings.filter((w): w is string => typeof w === 'string') : [],
    source,
    expanded: Array.isArray(raw.expanded)
      ? raw.expanded.filter((p): p is number[] => Array.isArray(p) && p.every((v) => typeof v === 'number' && Number.isInteger(v) && v >= 0))
      : [],
  };
}

/** 素材 API の行 (snake / camel の generation_metadata) から LayerStack を取り出す */
export function layerStackFromMaterial(material: unknown): LayerStack | null {
  if (!isObj(material)) return null;
  const meta = material.generation_metadata ?? material.generationMetadata;
  if (!isObj(meta)) return null;
  return parseLayerStack(meta.layer_stack);
}

/**
 * 入稿ゲート (§3.6-8) を通った LayerStack か (`integrity.checked === true`)。backend の generate が
 * 400 にする素材 (§6.1) を FE でもレイヤー素材として扱わないために使う。
 */
export function isCheckedLayerStack(stack: Pick<LayerStack, 'integrity'> | null | undefined): boolean {
  return !!stack && stack.integrity?.checked === true;
}

/**
 * レイヤーの矩形を合成 PNG (= カンバス全体) に対する割合 (%) にする。合成プレビュー上のハイライト矩形に使う。
 * カンバス座標から直接求める ((x - originX) / canvas.width)。合成 PNG は常にカンバス全体を描くので、
 * pxPerUnit (合成 PNG の丸めを含む) を経由しない方がずれない。
 */
export function layerRectPercent(
  stack: Pick<LayerStack, 'canvas'>,
  layer: Pick<LayerStackLayer, 'x' | 'y' | 'width' | 'height'>,
): { left: number; top: number; width: number; height: number } {
  const { canvas } = stack;
  return {
    left: ((layer.x - canvas.originX) / canvas.width) * 100,
    top: ((layer.y - canvas.originY) / canvas.height) * 100,
    width: (layer.width / canvas.width) * 100,
    height: (layer.height / canvas.height) * 100,
  };
}

/**
 * レイヤー行の小さなサムネイル: 合成 PNG をレイヤーの矩形が box (正方形 px) に収まるよう拡大し、
 * 矩形の中心が box の中心に来る位置へずらす (<img> の absolute 配置用・寸法は合成 PNG の表示 px)。
 * 合成画像の切り抜きなので、重なっている他レイヤーも写る (簡易表示)。
 */
export function layerThumbnailStyle(
  stack: Pick<LayerStack, 'canvas' | 'composite'>,
  layer: Pick<LayerStackLayer, 'x' | 'y' | 'width' | 'height'>,
  box: number,
): { width: number; height: number; left: number; top: number } {
  const rect = layerRectPercent(stack, layer);
  // 合成 PNG の実寸 (px) 上の矩形
  const cw = stack.composite.width;
  const ch = stack.composite.height;
  const rx = (rect.left / 100) * cw;
  const ry = (rect.top / 100) * ch;
  const rw = Math.max(1, (rect.width / 100) * cw);
  const rh = Math.max(1, (rect.height / 100) * ch);
  const scale = Math.min(box / rw, box / rh);
  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    width: round(cw * scale),
    height: round(ch * scale),
    left: round(-rx * scale + (box - rw * scale) / 2),
    top: round(-ry * scale + (box - rh * scale) / 2),
  };
}

/** モーダル・設定欄で出すレイヤー素材の名前 (素材名 → 元ファイル名 → 既定) */
export function layerMaterialDisplayName(
  asset: { name?: string | null },
  stack: Pick<LayerStack, 'source'>,
): string {
  return asset.name?.trim() || stack.source?.filename || 'レイヤー素材';
}

/** ラスタのレイヤー (kind 'png') の行に出すバッジと説明 (§5.2) */
export const RASTER_LAYER_BADGE_LABEL = '画像';
export const RASTER_LAYER_BADGE_TITLE = '画像を含むためラスタとして扱います。色の一時変更・線の描画は使えません';

export function layerCountLabel(stack: Pick<LayerStack, 'layers'>): string {
  return `${stack.layers.length} レイヤー`;
}

/** 展開で生じたレイヤーか (親グループが展開済み → 「グループに戻す」ができる) */
export function isCollapsibleLayer(stack: Pick<LayerStack, 'expanded'>, layer: Pick<LayerStackLayer, 'path'>): boolean {
  if (!layer.path || layer.path.length < 2) return false;
  const parent = layer.path.slice(0, -1);
  return stack.expanded.some((p) => p.length === parent.length && p.every((v, i) => v === parent[i]));
}
